import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import PanelDetails from './panel-details';
import { COMPONENT_NAME } from './panel-details/shared';
import { Context, IListItems } from './panel-details/types/timeline/shared/context';
import CoreSplitter from '@components-clean/core/core-splitter';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { getTimelinePanelListItems, ManagePanelSplitterPanes } from '@pages-clean/admin/study/manage-panels/utils';
import { PanelType, TimelineAxisType, TimelineFieldLogicalOperator } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useLocalState } from '@hooks/index';
import Constants, { SplitterOrientation } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { GET_PANELS } from '@queries/central-monitoring';
import {
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_dateSeries_DateSeries_rowData_SeriesRowData_rule_TimelineRule as seriesRowData,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_dateSeries_DateSeries,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_numericSeries_NumericSeries,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis,
    getPanels,
} from '@queries/generated/central-monitoring.generated';

interface ITimelinePanel {
    id: string;
    active: boolean;
    plotTitle: string;
    isDualAxis: boolean;
    created: string;
    createdByFullName: string;
    updated: string;
    updatedByFullName: string;
    leftAxis: {
        label: string;
        unit: string;
        type: string;
        series1?: ITimelinePanelSeries;
        series2?: ITimelinePanelSeries;
        series3?: ITimelinePanelSeries;
        series4?: ITimelinePanelSeries;
    };
    rightAxis: {
        label: string;
        unit: string;
        type: string;
        series1?: ITimelinePanelSeries;
        series2?: ITimelinePanelSeries;
        series3?: ITimelinePanelSeries;
        series4?: ITimelinePanelSeries;
    };
}

interface ITimelinePanelSeries {
    label: string;
    rowData: string;
    filter?: string;
    type: string;
    startField: string;
    endField?: string;
    thresholdType: 'Numeric' | 'Field' | 'N/A';
    upperThreshold: string;
    lowerThreshold: string;
}

interface IProps {
    organisationId: string;
    studyId: string;
}

const TimelineTabContent = ({ organisationId, studyId }: IProps) => {
    const { state } = useLocalState();
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.grid.timeline' });
    const { t: tCommon } = useTranslation('admin', { keyPrefix: 'managePanels' });
    const user = useAuthorization();

    const canEdit = !!user?.canWrite?.({
        module: Constants.Modules.SM,
        element: Constants.DataElements.SubjectMonitoring,
        permission: Permissions.ReadWrite,
    });

    if (!state?.cm2DataSources) return <Loading />;

    const {
        data,
        error,
        refetch,
        loading: isLoading,
    } = useQuery<getPanels>(GET_PANELS, {
        variables: {
            organisationId,
            studyId,
        },
    });

    const TIMELINE_COLUMNS: GridColumnProps[] = [
        {
            field: 'active',
            title: t('columns.active'),
            id: 'panel-active',
            minWidth: 100,
            cells: { data: YesNoCell },
            filter: 'boolean',
            lockedByDefault: true,
            customColumn: true,
        },
        {
            field: 'plotTitle',
            title: t('columns.plotTitle'),
            id: 'panel-plotTitle',
            minWidth: 200,
            lockedByDefault: true,
            customColumn: true,
        },
        {
            field: 'isDualAxis',
            title: t('columns.isDualAxis'),
            id: 'panel-isDualAxis',
            cells: { data: YesNoCell },
            filter: 'boolean',
            minWidth: 100,
            customColumn: true,
        },
        {
            title: t('columns.leftAxis.title'),
            field: 'leftAxis.title',
            id: 'panel-leftAxis.title',
            children: [
                {
                    field: 'leftAxis.label',
                    title: t('columns.leftAxis.label'),
                    minWidth: 200,
                    customColumn: true,
                },
                {
                    field: 'leftAxis.unit',
                    title: t('columns.leftAxis.unit'),
                    minWidth: 150,
                    customColumn: true,
                },
                {
                    field: 'leftAxis.type',
                    title: t('columns.leftAxis.type'),
                    minWidth: 100,
                    customColumn: true,
                },
                ...Array(4)
                    .fill(0)
                    .flatMap((_, index) => [
                        {
                            field: `leftAxis.series${index + 1}.label`,
                            title: t(`columns.leftAxis.series${index + 1}Label`),
                            minWidth: 200,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.type`,
                            title: t(`columns.leftAxis.series${index + 1}Type`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.rowData`,
                            title: t(`columns.leftAxis.series${index + 1}RowData`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.filter`,
                            title: t(`columns.leftAxis.series${index + 1}Filter`),
                            minWidth: 300,
                            customColumn: true,
                            cells: {
                                data: (props: GridCustomCellProps) => {
                                    const panel = props.dataItem as ITimelinePanel;
                                    const series = panel.leftAxis[`series${(index + 1) as 1 | 2 | 3 | 4}`] as
                                        | ITimelinePanelSeries
                                        | undefined;

                                    return <td style={{ whiteSpace: 'pre-wrap' }}>{series?.filter}</td>;
                                },
                            },
                        },
                        {
                            field: `leftAxis.series${index + 1}.startField`,
                            title: t(`columns.leftAxis.series${index + 1}StartField`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.endField`,
                            title: t(`columns.leftAxis.series${index + 1}EndField`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.thresholdType`,
                            title: t(`columns.leftAxis.series${index + 1}ThresholdType`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.upperThreshold`,
                            title: t(`columns.leftAxis.series${index + 1}UpperThreshold`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `leftAxis.series${index + 1}.lowerThreshold`,
                            title: t(`columns.leftAxis.series${index + 1}LowerThreshold`),
                            minWidth: 150,
                            customColumn: true,
                        },
                    ]),
            ],
        },
        {
            title: t('columns.rightAxis.title'),
            field: 'rightAxis.title',
            id: 'panel-rightAxis.title',
            children: [
                {
                    field: 'rightAxis.label',
                    title: t('columns.rightAxis.label'),
                    minWidth: 200,
                    customColumn: true,
                },
                {
                    field: 'rightAxis.unit',
                    title: t('columns.rightAxis.unit'),
                    minWidth: 150,
                    customColumn: true,
                },
                {
                    field: 'rightAxis.type',
                    title: t('columns.rightAxis.type'),
                    minWidth: 100,
                    customColumn: true,
                },
                ...Array(4)
                    .fill(0)
                    .flatMap((_, index) => [
                        {
                            field: `rightAxis.series${index + 1}.label`,
                            title: t(`columns.rightAxis.series${index + 1}Label`),
                            minWidth: 200,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.type`,
                            title: t(`columns.rightAxis.series${index + 1}Type`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.rowData`,
                            title: t(`columns.rightAxis.series${index + 1}RowData`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.filter`,
                            title: t(`columns.rightAxis.series${index + 1}Filter`),
                            minWidth: 300,
                            customColumn: true,
                            cells: {
                                data: (props: GridCustomCellProps) => {
                                    const panel = props.dataItem as ITimelinePanel;
                                    const series = panel.rightAxis[`series${(index + 1) as 1 | 2 | 3 | 4}`] as
                                        | ITimelinePanelSeries
                                        | undefined;

                                    return <td style={{ whiteSpace: 'pre-wrap' }}>{series?.filter}</td>;
                                },
                            },
                        },
                        {
                            field: `rightAxis.series${index + 1}.startField`,
                            title: t(`columns.rightAxis.series${index + 1}StartField`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.endField`,
                            title: t(`columns.rightAxis.series${index + 1}EndField`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.thresholdType`,
                            title: t(`columns.rightAxis.series${index + 1}ThresholdType`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.upperThreshold`,
                            title: t(`columns.rightAxis.series${index + 1}UpperThreshold`),
                            minWidth: 150,
                            customColumn: true,
                        },
                        {
                            field: `rightAxis.series${index + 1}.lowerThreshold`,
                            title: t(`columns.rightAxis.series${index + 1}LowerThreshold`),
                            minWidth: 150,
                            customColumn: true,
                        },
                    ]),
            ],
        },
        {
            title: tCommon('panel-createdBy'),
            field: 'createdByFullName',
            id: 'panel-createdBy',
            minWidth: 180,
            filter: 'text',
            customColumn: true,
        },
        {
            title: tCommon('panel-created'),
            field: 'created',
            id: 'panel-created',
            filter: 'date',
            minWidth: 145,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            customColumn: true,
        },
        {
            title: tCommon('panel-updatedBy'),
            field: 'updatedByFullName',
            id: 'panel-updatedBy',
            minWidth: 180,
            filter: 'text',
            customColumn: true,
        },
        {
            title: tCommon('panel-updated'),
            field: 'updated',
            id: 'panel-updated',
            filter: 'date',
            minWidth: 145,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            customColumn: true,
        },
    ];

    const listItems: IListItems = useMemo(
        () => getTimelinePanelListItems(state),
        [state!.cm2DataSources, state!.cm2Fields],
    );

    const panels: ITimelinePanel[] = useMemo(() => {
        if (!data || !data.panels) return [];

        return data.panels
            .filter((panel) => !!panel)
            .filter((panel) => panel.panelType === PanelType.TIMELINE)
            .map((panel) => {
                const timeline = panel.timeline!;
                const { leftAxis, rightAxis } = timeline;

                const getSeries = (
                    axis:
                        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis
                        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis
                        | undefined,
                    colPosition: number,
                ) => {
                    if (!axis) return undefined;

                    const series =
                        axis.type === TimelineAxisType.DATE
                            ? axis.dateSeries?.[colPosition]
                            : axis.numericSeries?.[colPosition];

                    if (!series) return undefined;

                    const getFilterDescription = (rule?: seriesRowData | null) => {
                        if (!rule) return undefined;

                        const getRule = (fieldId?: string | null, operator?: string | null, value?: string | null) => {
                            const field = listItems.fields.find((item) => item.id === fieldId);

                            const renderValue = () => {
                                if (!operator || !field?.fieldType) return '';

                                if (
                                    operator !== TimelineFieldLogicalOperator.IS_BLANK &&
                                    operator !== TimelineFieldLogicalOperator.IS_NOT_BLANK
                                ) {
                                    switch (field.fieldType) {
                                        case 'BOOL':
                                            return ` ${value ? 'Yes' : 'No'}`;
                                        case 'DATE':
                                        case 'PARTIAL_DATE':
                                            return ` ${stringDateWithoutTimeFromUtc(value?.toString())}`;
                                        case 'TEXT':
                                            return ` "${value}"`;
                                        default:
                                            return ` ${value}`;
                                    }
                                }

                                return '';
                            };

                            return (
                                `${field?.text} ` +
                                `${listItems.operators.find((item) => item.id === operator)?.text} ${renderValue()}`
                            );
                        };

                        const lines = [getRule(rule.field1, rule.operator1, rule.value1)];
                        if (rule.logicalOperator) {
                            lines.push(`${listItems.ruleOperators.find((o) => o.id === rule.logicalOperator)?.text}`);
                            lines.push(getRule(rule.field2, rule.operator2, rule.value2));
                        }
                        return lines.join(' ');
                    };

                    const flatSeries: ITimelinePanelSeries = {
                        label: series.label,
                        type: '',
                        rowData: listItems.rowData.find((item) => item.id === series.rowData.source)?.text ?? '[?]',
                        filter: getFilterDescription(series.rowData.rule),
                        startField: '',
                        thresholdType: 'N/A',
                        upperThreshold: 'N/A',
                        lowerThreshold: 'N/A',
                    };

                    if (axis.type === TimelineAxisType.DATE) {
                        const dateSeries =
                            series as getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_dateSeries_DateSeries;
                        flatSeries.type =
                            listItems.dateSeriesTypes.find((item) => item.id === series.type)?.text ?? '[?]';
                        flatSeries.startField =
                            listItems.fields.find((item) => item.id === dateSeries.startField)?.text ?? '[?]';
                        flatSeries.endField = listItems.fields.find((item) => item.id === dateSeries.endField)?.text;
                    }

                    if (axis.type === TimelineAxisType.NUMERIC) {
                        const numericSeries =
                            series as getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_numericSeries_NumericSeries;
                        flatSeries.type =
                            listItems.numericSeriesTypes.find((item) => item.id === series.type)?.text ?? '[?]';

                        if (numericSeries.threshold != null) {
                            if (numericSeries.threshold?.value) {
                                flatSeries.thresholdType = 'Numeric';
                                flatSeries.upperThreshold = `${numericSeries.threshold.value.upper ?? '-'}`;
                                flatSeries.lowerThreshold = `${numericSeries.threshold.value.lower ?? '-'}`;
                            } else if (numericSeries.threshold?.field) {
                                flatSeries.thresholdType = 'Field';
                                flatSeries.upperThreshold =
                                    listItems.fields.find((item) => item.id === numericSeries.threshold!.field!.upper)
                                        ?.text ?? '-';
                                flatSeries.lowerThreshold =
                                    listItems.fields.find((item) => item.id === numericSeries.threshold!.field!.lower)
                                        ?.text ?? '-';
                            }
                        }
                    }

                    return flatSeries;
                };

                const p: ITimelinePanel = {
                    id: panel.id,
                    active: panel.active ?? false,
                    plotTitle: panel.name || '',
                    updatedByFullName: `${panel?.updatedBy?.firstName} ${panel?.updatedBy?.lastName}`,
                    createdByFullName: `${panel?.createdBy?.firstName} ${panel?.createdBy?.lastName}`,
                    created: panel?.created,
                    updated: panel?.updated,
                    isDualAxis: !!timeline.rightAxis,
                    leftAxis: {
                        label: timeline.leftAxis.title,
                        unit: timeline.leftAxis.unit ?? '',
                        type: listItems.axisTypes.find((item) => item.id === leftAxis?.type)?.text ?? '',
                        series1: getSeries(timeline.leftAxis, 0),
                        series2: getSeries(timeline.leftAxis, 1),
                        series3: getSeries(timeline.leftAxis, 2),
                        series4: getSeries(timeline.leftAxis, 3),
                    },
                    rightAxis: {
                        label: timeline?.rightAxis?.title ?? '',
                        unit: timeline?.rightAxis?.unit ?? '',
                        type: listItems.axisTypes.find((item) => item.id === rightAxis?.type)?.text ?? '',
                        series1: getSeries(timeline.rightAxis ?? undefined, 0),
                        series2: getSeries(timeline.rightAxis ?? undefined, 1),
                        series3: getSeries(timeline.rightAxis ?? undefined, 2),
                        series4: getSeries(timeline.rightAxis ?? undefined, 3),
                    },
                };

                return p;
            });
    }, [data]);

    const [justCreatedPanelRef, setJustCreatedPanelRef] = React.useState<string | undefined>(undefined);
    const [selectedPanel, setSelectedPanel] = React.useState<ITimelinePanel | undefined>(undefined);
    const [isCreating, setIsCreating] = React.useState<boolean>(false);

    useEffect(() => {
        if (!panels || !justCreatedPanelRef) return;

        const newlyCreatedPanel = panels.find((p) => p.id === justCreatedPanelRef);
        setSelectedPanel(newlyCreatedPanel);
        setJustCreatedPanelRef(undefined);
    }, [panels]);

    const [filter, setFilter] = React.useState<CompositeFilterDescriptor | undefined>(undefined);

    const context = useMemo(
        () => ({
            organisationId,
            studyId,
            listItems,
            hasFilters: !!filter,
        }),
        [organisationId, studyId, listItems, filter],
    );

    const [gridKey, setGridKey] = React.useState<number>(0);

    const clearFilters = () => {
        setFilter(undefined);
        setGridKey((prev) => prev + 1);
    };
    const { componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    let content;

    if (error) {
        content = <InfoField type="Error" message={t('loadFailure')} />;
    } else if (isLoading) {
        content = <Loading />;
    } else {
        content = (
            <div className="content">
                <CoreSplitter
                    className="!k-h-full"
                    alignmentValues={ManagePanelSplitterPanes}
                    orientation={SplitterOrientation.vertical}
                    data-testid="manage-panels-content"
                >
                    <Grid
                        key={`grid-${gridKey}`}
                        title={t('title')}
                        testDataId="panels-grid"
                        maxHeight="auto"
                        selectable={{
                            enabled: true,
                            mode: 'single',
                        }}
                        dataItemKey="id"
                        scrollable="scrollable"
                        exportFilename={`Timeline Panels_${state?.selectedStudy?.studyNumber}_${new Date()
                            .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            .replaceAll(' ', '-')}`}
                        columns={TIMELINE_COLUMNS}
                        addCallback={async () => {
                            setSelectedPanel(undefined);
                            setIsCreating(true);
                        }}
                        data={panels}
                        canEdit={canEdit}
                        defaultSelectedItem={selectedPanel}
                        preSelectionChange={async (event) => {
                            if (componentHasChanges(COMPONENT_NAME)) {
                                const confirmed = await confirmDiscardChanges();
                                if (!confirmed) {
                                    return false;
                                }
                            }
                            setSelectedPanel(event.startDataItem as ITimelinePanel);
                            return true;
                        }}
                        onSelectionChange={(event) => {
                            setSelectedPanel(event.startDataItem as ITimelinePanel);
                        }}
                        filter={filter}
                        onFilterChange={async (newFilter) => {
                            setFilter(newFilter.filter);
                        }}
                        suppressAddFilterClearWarning
                    />
                    <PanelDetails
                        createData={
                            isCreating
                                ? {
                                      type: 'Timeline',
                                      onCreate: async (newPanelRef: string) => {
                                          setIsCreating(false);
                                          setJustCreatedPanelRef(newPanelRef);
                                          await refetch();
                                          clearFilters();
                                      },
                                      onCancel: () => {
                                          setIsCreating(false);
                                      },
                                  }
                                : undefined
                        }
                        panel={
                            selectedPanel && {
                                id: selectedPanel.id,
                                type: PanelType.TIMELINE,
                            }
                        }
                        canEdit={canEdit}
                    />
                </CoreSplitter>
            </div>
        );
    }

    return (
        <Context.Provider value={context}>
            <div className="model-panels">{content}</div>
        </Context.Provider>
    );
};

export default TimelineTabContent;
